import React, { useEffect, useRef, useState } from 'react';
import './NewAccordanceForm.scss';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useStyles } from './NewAccordanceForm.Styles';
import RequiredErrorMessage from '../../../../Util/Errors/RequiredErrorMessage/RequiredErrorMessage';
import { AccordanceType } from '../../../../../models/AccordanceType';
import DateSelector from './DateSelector';
import SelectOption from '../../../../../models/SelectOption';
import CustomDatePicker from '../../../../Util/CustomDatePicker/CustomDatePicker';
import SearchUsers from '../../../../Util/SearchUsers/SearchUsers';
import { AccordanceState } from '../../../../../models/AccordanceState';
import { OptionWithCheckBox } from './OptionWithCheckBox/OptionWithCheckBox';
import { TagOptionSelected } from './Tags/TagOptionSelected';
import ImproveWithAIButton from '../../../../Util/Buttons/ImproveWithAIButton/ImproveWithAIButton';
import { getIASuggestion, IASuggestionResponse, IASuggestionPayload, logDecisionIASuggestion } from '../../../../../api/accordanceApi';
import { IA_ERROR_REQUEST_TYPE } from './IAImproveText/IAErrorRequestType';
import { ActionTypeAI, FieldTypeAi } from '../../../../../enums/IA-suggestionEnum';
import { useSelector } from 'react-redux';
import { ACCEPTED_IA_SUGGESTION, REJECTED_IA_SUGGESTION } from '../../../../Projects/ProjectsReport/utils/Constants/AlertStatusConstants';

const SUPPORT_DOCUMENT_LINK = 'https://idbg.sharepoint.com/teams/ez-LEG/Regulations/Current%20Regulations/ENG/OA-430%20Substantial%20and%20Fundamental%20Changes%20to%20Operations.pdf';
const MAX_LENGHT_DESCRIPTION = 500;

const hasMinimumInputLength = (value?: string) => (value?.trim().length || 0) >= 60;

export const displayLabel = (option: any) => {
  return option.label || '';
};

export const displayName = (option: any) => {
  return option.name || option.nameProduct;
};

type Props = {
  isMulti?: boolean;
  criticalIssueList?: any;
  criticalIssue?: any;
  handleCriticalIssueChange?: any;
  formErrors?: any;
  category?: any;
  expirationDate?: any;
  revisionDate?: any;
  criticalIssueDescription?: string;
  handleCriticalIssueDescription: Function;
  setFormErrors?: any;
  setRevisionDate?: any;
  setExpirationDate?: any;
  setSemesterOption?: any;
  semesterOption: SelectOption | null;
  accordanceFormType: AccordanceType;
  accordanceDate?: any;
  handleAccordanceDate: Function;
  definedAgreement?: any;
  linkedProduct?: any;
  handleDefinedAgreementChange?: any;
  handleLinkedProduct?: any;
  accordanceDefinitionForm?: any;
  productForm?: any;
  validateTextareaCritical?: any;
  expirationDateSingle?: any;
  handleExpirationDateSingle?: any;
  handleDescriptionSingle?: any;
  descriptionSingle?: any;
  validateTextareaDescriptionSingle?: any;
  responsibleSingle?: any;
  handleIemSelectedSingle?: any;
  editAccordanceDataSingle?: any;
  accordanceStateSingle?: any;
  isSubstantialAgreement?: boolean;
  substantialTypeChange?: any;
  handleSubstantialTypeChange?: any;
  PMBLink?: string;
  handlePMBLink?: any;
  substantialChangeTypeOptions?: any[];
  isRequiredChangeRevisionDate?: boolean;
  setAlertConfig?: Function;
};

const SharedInputs = (props: Props) => {
  const { t } = useTranslation();

  const {
    isMulti,
    criticalIssueList,
    criticalIssue,
    handleCriticalIssueChange,
    formErrors,
    category,
    expirationDate,
    revisionDate,
    criticalIssueDescription,
    handleCriticalIssueDescription,
    setFormErrors,
    setRevisionDate,
    setExpirationDate,
    setSemesterOption,
    semesterOption,
    accordanceFormType,
    accordanceDate,
    handleAccordanceDate,
    definedAgreement,
    linkedProduct,
    handleDefinedAgreementChange,
    handleLinkedProduct,
    accordanceDefinitionForm,
    productForm,
    validateTextareaCritical,
    expirationDateSingle,
    handleExpirationDateSingle,
    handleDescriptionSingle,
    descriptionSingle,
    validateTextareaDescriptionSingle,
    responsibleSingle,
    handleIemSelectedSingle,
    editAccordanceDataSingle,
    accordanceStateSingle,
    isSubstantialAgreement,
    substantialTypeChange,
    handleSubstantialTypeChange,
    PMBLink,
    handlePMBLink,
    substantialChangeTypeOptions,
    isRequiredChangeRevisionDate,
    setAlertConfig,
  } = props;

  const currentProjectId = useSelector<any, number>(state => state.currentProject?.id || 0);

  const isNotRequired = !!editAccordanceDataSingle;
  const classes = useStyles({ isNotRequired });
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const criticalIssueDescriptionRef = useRef<HTMLTextAreaElement>(null);
  const [showButtonIA, setShowButtonIA] = useState(false);
  const [isLoadingIA, setIsLoadingIA] = useState(false);
  const [IAResponse, setIAResponse] = useState<IASuggestionResponse | null>(null);
  const [IAErrorRequest, setIAErrorRequest] = useState<string | null>(null);
  const [isAppliedIASugestion, setIsAppliedIASugestion] = useState(false);

  useEffect(() => {
    setShowButtonIA(hasMinimumInputLength(descriptionSingle));
  }, [descriptionSingle]);

  const handleDescriptionChange = (value: string) => {
    validateTextareaDescriptionSingle(value);
    handleDescriptionSingle(value);
    setShowButtonIA(hasMinimumInputLength(value));

    if (IAResponse) setIAResponse(null);
    if (IAErrorRequest) setIAErrorRequest(null);
    if (isAppliedIASugestion) setIsAppliedIASugestion(false);

    if (descriptionRef.current) {
      descriptionRef.current.style.height = 'auto';
      descriptionRef.current.style.height = descriptionRef.current.scrollHeight + 'px';
    }
  };

  const handleCriticalIssueDescriptionChange = (value: string) => {
    validateTextareaCritical(value);
    handleCriticalIssueDescription(value);

    if (criticalIssueDescriptionRef.current) {
      criticalIssueDescriptionRef.current.style.height = 'auto';
      criticalIssueDescriptionRef.current.style.height = criticalIssueDescriptionRef.current.scrollHeight + 'px';
    }
  };

  const handleImproveWithAI = async (originalText: string, field: string) => {
    setIsLoadingIA(true);

    const payload: IASuggestionPayload = {
      QuestionOriginalText: originalText,
      actionType: ActionTypeAI.Generated,
      fieldType: FieldTypeAi.AccordancesDescription,
      entityType: 'Accordances',
      PreviousSuggestion: '',
    }

    await getIASuggestion(currentProjectId, payload)
    .then(response => {
      setIsLoadingIA(false);
      setIAResponse(response);
    })
    .catch((error) => {
      setIsLoadingIA(false);
      setIAErrorRequest(t(IA_ERROR_REQUEST_TYPE[error?.response?.status] || 'ia_generic_error'));
    });
  }

  const handleUseIAResponse = () => {
    if (!IAResponse?.answer) {
      return;
    }

    handleDescriptionChange(IAResponse.answer);
    logDecisionIASuggestion(IAResponse, ActionTypeAI.Accepted);
    setAlertConfig && setAlertConfig(ACCEPTED_IA_SUGGESTION);
    setIsAppliedIASugestion(true);
    setIAResponse(null);
  };

  const handleDiscardIAResponse = async () => {
    await logDecisionIASuggestion(IAResponse!, ActionTypeAI.Discarded);
    setAlertConfig && setAlertConfig(REJECTED_IA_SUGGESTION);
    setIAResponse(null);
  };

  return (
    <section className='common-inputs-content'>
      <div className='common-inputs-header'>
        {!isMulti && <h3>{t('new_general_information_of_the_agreement')}</h3>}
        <span>{t('all_fields_required_for_sending_approval')}</span>
      </div>
      <div className='common-inputs-row'>
        <div className="horizontal-row">
          <div className="label-select-container">
            <p className="label">{t('type_of_accordance')}</p>
            <Autocomplete
              id="combo-box-demo"
              value={definedAgreement}
              onChange={handleDefinedAgreementChange}
              options={accordanceDefinitionForm}
              getOptionLabel={displayName}
              renderInput={params => <TextField {...params} placeholder={t('new_write_select')} fullWidth />}
              classes={formErrors?.definedAgreement ? classes : {}}
              noOptionsText={t('no_results')}
              getOptionSelected={(option, value) => option.id === value.id}
            />
            {formErrors?.definedAgreement && <RequiredErrorMessage isNotRequired={isNotRequired} />}
          </div>
        </div>
        {isSubstantialAgreement && (
          <div className="horizontal-row substantial-inputs">
            <div className="label-select-container multiple-tags" style={{ width: '50%' }}>
              <p className="label">{t('substantial_fundamental_changes')}</p>
              <Autocomplete
                id="substantial-agreement-type"
                value={substantialTypeChange}
                onChange={handleSubstantialTypeChange}
                options={substantialChangeTypeOptions || []}
                getOptionLabel={displayName}
                renderInput={params => <TextField {...params} placeholder={substantialTypeChange?.length !== 0 ? ' ' : t('substantial_type_placeholder')} fullWidth />}
                classes={formErrors?.substantialTypeChange ? classes : {}}
                noOptionsText={t('no_results')}
                getOptionSelected={(option, value) => option.id === value.id}
                limitTags={3}
                renderOption={(option, { selected }) => (<OptionWithCheckBox option={option} selected={selected} />)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <TagOptionSelected key={index} label={option.name} getTagProps={getTagProps} index={index} />
                  ))
                }
                multiple
                fullWidth
                disableCloseOnSelect
              />
              <p className="label-observation">{t('substantial_type_info') + ' '}
                <a
                  href={SUPPORT_DOCUMENT_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link"
                >
                  {'OA-430'}
                </a>
              </p>
              {formErrors?.substantialTypeChange && <RequiredErrorMessage isNotRequired={isNotRequired} />}
            </div>

            <div className="label-select-container" style={{ width: '50%' }}>
              <p className="label">{t('pmb')}</p>
              <TextField
                className={`custom-input-form-check ${PMBLink?.trim() && !formErrors?.pmbLink && !formErrors?.pmbLinkInvalid ? 'is-valid-link' : formErrors?.pmbLinkInvalid ? 'is-invalid-link' : ''
                  } ${isNotRequired && formErrors?.pmbLink && !formErrors?.pmbLinkInvalid ? 'is-not-required' : ''
                  }`}
                variant="outlined"
                fullWidth
                placeholder={t('pmb_link_placeholder')}
                onChange={e => handlePMBLink(e.target.value)}
                onBlur={e => handlePMBLink(e.target.value, true)}
                value={PMBLink}
                error={formErrors?.pmbLink || formErrors?.pmbLinkInvalid}
                size='small' />
              <p className="label-observation">
                {formErrors?.pmbLinkInvalid ? (
                  <span className="invalid-link">{t('pmb_link_validation')}</span>
                ) : (PMBLink?.trim() && !formErrors?.pmbLinkInvalid) ? (
                  <span className="valid-link">
                    {t('valid_link')}&nbsp;
                    <a
                      href={PMBLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link"
                    >
                      {'EZShare-' + PMBLink.split('=')[1]}
                    </a>
                  </span>
                ) : t('pmb_link_info')}
              </p>
              {formErrors?.pmbLink && <RequiredErrorMessage isNotRequired={isNotRequired} />}
            </div>
          </div>
        )
        }
        <div className="horizontal-row agreement-date-selector-container">
          <DateSelector
            accordanceFormType={accordanceFormType}
            revisionDate={revisionDate}
            setFormErrors={setFormErrors}
            formErrors={formErrors}
            setRevisionDate={setRevisionDate}
            expirationDate={expirationDate}
            setExpirationDate={setExpirationDate}
            setSemesterOption={setSemesterOption}
            semesterOption={semesterOption}
            accordanceDate={accordanceDate}
            handleAccordanceDate={handleAccordanceDate}
            minDate={new Date()}
            isRequiredChangeRevisionDate={isRequiredChangeRevisionDate}
          />
          {!isMulti ? (
            <div className={"label-select-container" + (isSubstantialAgreement ? " disabled" : "")}>
              <CustomDatePicker
                date={expirationDateSingle}
                label={t('due_date')}
                setDate={handleExpirationDateSingle}
                error={formErrors?.expirationDate}
                isNotRequired={isNotRequired}
                minDate={revisionDate}
                disabled={!revisionDate || isSubstantialAgreement}
                placeholderComplete={true}
              />
              {formErrors?.expirationDate && <RequiredErrorMessage isNotRequired={isNotRequired} />}
              {isSubstantialAgreement && <p className="label-observation">{t('due_date_info')}</p>}
            </div>
          ) : null}
        </div>
        {!isMulti ? (
          <>
            <div className="horizontal-row">
              <div className="label-select-container text-area">
                <div className="label-selec-number-caracteres">
                  <p className="label">{`${t('accordance_description')}`}</p>
                  <p>{`${descriptionSingle?.length === undefined ? 0 : descriptionSingle?.length}/${MAX_LENGHT_DESCRIPTION}`}</p>
                </div>

                <div className={`content-div-with-ai ${formErrors?.description ? (isNotRequired ? 'warning-border' : 'error-border') : ''}`}>
                  <textarea
                    ref={descriptionRef}
                    className="textarea-with-ai"
                    maxLength={MAX_LENGHT_DESCRIPTION}
                    value={descriptionSingle}
                    onChange={e => handleDescriptionChange(e.target.value)}
                    onBlur={e => handleDescriptionSingle(e.target.value)}
                    placeholder={t('description_agreement_responsibility_and_result')}
                  ></textarea>

                  {!isAppliedIASugestion &&
                    <ImproveWithAIButton
                      disabled={!showButtonIA}
                      isLoading={isLoadingIA}
                      handleClick={() => handleImproveWithAI(descriptionSingle, 'accordance_description')}
                      customText={isLoadingIA ? t('analizing').toUpperCase() : undefined}
                      IAResponse={IAResponse}
                      IAErrorRequest={IAErrorRequest}
                      suggestedText={IAResponse?.answer}
                      textToValidate={descriptionSingle}
                      onUse={handleUseIAResponse}
                      onDiscard={handleDiscardIAResponse}
                      onRetry={() => {
                        setIAErrorRequest(null);
                        handleImproveWithAI(descriptionSingle, 'accordance_description');
                      }}
                      onCancel={() => setIAErrorRequest(null)}
                    />
                  }
                </div>
                {formErrors?.description && <RequiredErrorMessage isNotRequired={isNotRequired} />}
              </div>
            </div>

            {
              !isSubstantialAgreement && (
                <div className="horizontal-row">
                  <div className="label-select-container">
                    <p className="label">{t('new_bid_responsible')}</p>
                    {editAccordanceDataSingle && accordanceStateSingle !== AccordanceState.DRAFT ? (
                      <span className="label">
                        {editAccordanceDataSingle?.personInCharge?.name} {editAccordanceDataSingle?.personInCharge?.lastName}
                      </span>
                    ) : (
                        <SearchUsers
                          selected={responsibleSingle}
                          itemSelected={handleIemSelectedSingle}
                          error={formErrors?.responsible}
                          isNotRequired={isNotRequired}
                        />
                      )}
                    <p className="label-observation">{t('new_assign_to_the_manager')}</p>
                    {formErrors?.responsible && <RequiredErrorMessage isNotRequired={isNotRequired} />}
                  </div>
                </div>
              )
            }
          </>
        ) : (
            <>
              <div className="horizontal-row">
                <div className="label-select-container">
                  <p className="label">{t('critical_issue')}</p>
                  <Autocomplete
                    id="search-criticl-issues"
                    options={criticalIssueList?.filter(critical => critical.categoryName !== null)}
                    getOptionLabel={displayLabel}
                    value={criticalIssue}
                    onChange={handleCriticalIssueChange}
                    renderInput={params => <TextField placeholder={t('select_item')} {...params} fullWidth />}
                    noOptionsText={t('no_results')}
                    classes={formErrors?.criticalIssue ? classes : {}}
                    getOptionSelected={(option, value) => option.id === value.id}
                  />
                  {formErrors?.criticalIssue && <RequiredErrorMessage isNotRequired={isNotRequired} />}
                </div>
                <div className="label-select-container multiple-tags" style={{ width: '100%' }}>
                  {productForm?.length !== 0 ? (
                    <>
                      <p className="label">{t('new_linked_product')}</p>
                      <Autocomplete
                        groupBy={option => `${t('new_component')} ${option.componentOrder}`}
                        multiple
                        value={linkedProduct}
                        id="checkboxes-tags-demo"
                        options={productForm}
                        onChange={handleLinkedProduct}
                        disableCloseOnSelect
                        noOptionsText={t('no_results')}
                        getOptionSelected={(option, value) => option.id === value.id}
                        getOptionLabel={displayName}
                        limitTags={3}
                        renderOption={(option, { selected }) => (
                          <OptionWithCheckBox
                            option={option}
                            selected={selected}
                          />)}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <TagOptionSelected key={index} label={option.name} getTagProps={getTagProps} index={index} />
                          ))
                        }
                        renderInput={params => (
                          <TextField {...params} placeholder={linkedProduct.length !== 0 ? ' ' : t('products')} fullWidth />
                        )}
                        classes={formErrors?.linkedProductsAccordance ? classes : {}}
                      />
                      {formErrors?.linkedProductsAccordance && <RequiredErrorMessage isNotRequired={isNotRequired} />}
                    </>
                  ) : null}
                </div>
              </div>
              <div className="horizontal-row">
                <div className="label-select-container text-area">
                  <div className="label-selec-number-caracteres">
                    <p className="label">{`${t('description_of_the_critical_issue')}`}</p>
                    <p>{`${criticalIssueDescription?.length === undefined ? 0 : criticalIssueDescription?.length}/${MAX_LENGHT_DESCRIPTION}`}</p>
                  </div>
                  <textarea
                    ref={criticalIssueDescriptionRef}
                    className={formErrors?.criticalIssueDescription ? (isNotRequired ? 'warning-border' : 'error-border') : ''}
                    maxLength={MAX_LENGHT_DESCRIPTION}
                    value={criticalIssueDescription}
                    onChange={e => handleCriticalIssueDescriptionChange(e.target.value)}
                    onBlur={e => handleCriticalIssueDescriptionChange(e.target.value)}
                    placeholder={t(
                      'new_enter_more_details_about_the_critical_issue_that_the_new_agreement_will_resolve_two',
                    )}
                  ></textarea>
                  {formErrors?.criticalIssueDescription && <RequiredErrorMessage isNotRequired={isNotRequired} />}
                </div>
              </div>
            </>
          )}
      </div>
    </section>
  );
};

export default SharedInputs;
